import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { i18n } from "@/i18n";
import { SelectionParametersFilterSendDescriptor } from "@/models/etc/parameter-types";
import { AnalyticsFormulaTokenDescriptor, ChartSettingsBaseDescriptor, ClientBarChartSplitType, ClientPieChartSplitType, FileBarChartSplitType, FilePieChartSplitType, JobBarChartSplitType, JobPieChartSplitType, PersonBarChartSplitType, PersonPieChartSplitType, ProjectBarChartSplitType, ProjectPieChartSplitType, SelectionFilterGroupSendDescriptor, StorageItemChartSplitType } from "@/models/etc/types"
import { arrayEquals, findRecursive, foreachRecursive } from "@/models/helpers/recursive";
import { SelectionDateStampsFilterSend, SelectionEntityWithRolesFilterSend, SelectionProjectStageFilterSend, SelectionStorageItemNameFilterDto } from "@/models/interfaces/entity-settings/filters/filters";
import { SelectionProjectStageFilterGroupSend } from "@/models/interfaces/entity-settings/filters/group-filters";
import { AnalyticsFormulaToken, AnalyticsTableFormula, SelectionParameterFilterBoolSend, SelectionParameterFilterDateStampsSend, 
    SelectionParameterFilterNumberSend, SelectionParameterFilterSend, SelectionParameterFilterStringSend, SelectionParameterFilterStringSetSend } from "@/models/interfaces/entity-settings/parameters/parameters";
import { isInstanceContainerEmpty } from "../../models/helpers/parameter-helpers";
import { ClientProjectBarTypes, SectorBarTypes } from "../graph";

/**
 * aucillary validation functions
 */

export enum ChartValidationErrors {
    DATE_RANGE = 0,
    NUMBER_BORDERS = 1,
    STEP_LENGTH = 2
}

export function validateFormula(formula: AnalyticsFormulaToken[], allowEmpty: boolean = false): boolean {
    let lbr_count = 0
    let rbr_count = 0

    let res = true

    formula.forEach((el, index) => {
        if (!el) { res = false; return; }
        switch (el.Descriptor) {
            case AnalyticsFormulaTokenDescriptor.Operation:
                if (index === formula.length-1) {
                    res = false
                    return
                }

                // previous token should be an operand or right bracket
                if (formula[index-1] && formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operand &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToFormula &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToParameter &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.GlobalLinkToTableFormula &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.TotalResultsCount &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.RightBracket) {
                    res = false
                    return
                }

                // next token should be an operand or left bracket
                if (formula[index+1] && formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operand &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToFormula &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToParameter &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.GlobalLinkToTableFormula &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.TotalResultsCount &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LeftBracket) {
                    res = false
                    return
                }
                break;
            case AnalyticsFormulaTokenDescriptor.Operand:
            case AnalyticsFormulaTokenDescriptor.LinkToFormula:
            case AnalyticsFormulaTokenDescriptor.LinkToParameter:
            case AnalyticsFormulaTokenDescriptor.GlobalLinkToTableFormula:
            case AnalyticsFormulaTokenDescriptor.TotalResultsCount:

                // previous token should be an operation or left bracket
                if (index > 0 && formula[index-1] && formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operation &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LeftBracket) {
                    res = false
                    return
                }

                // next token should be an operand or right bracket
                if (index < formula.length-1 && formula[index+1] && formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operation &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.RightBracket) {
                    res = false
                    return
                }
                break;
            case AnalyticsFormulaTokenDescriptor.LeftBracket:
                lbr_count++

                if (index === formula.length-1) {
                    res = false
                    return
                }

                // previous token should be an operation or another left bracket
                if (index > 0 && formula[index-1] && formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operation &&
                    formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LeftBracket) {
                    res = false
                    return
                }

                // next token should be an operand or another left bracket
                if (formula[index+1] && formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operand &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToParameter &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.TotalResultsCount &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToFormula &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.GlobalLinkToTableFormula &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.LeftBracket) {
                    res = false
                    return
                }
                break;
            case AnalyticsFormulaTokenDescriptor.RightBracket:
                rbr_count++
    
                if (index === 0) {
                    res = false
                    return
                }
    
                // previous token should be an operand or another right bracket
                if (formula[index-1] && formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operand &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToParameter &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.TotalResultsCount &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.LinkToFormula &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.GlobalLinkToTableFormula &&
                formula[index-1].Descriptor !== AnalyticsFormulaTokenDescriptor.RightBracket) {
                    res = false
                    return
                }

                // next token should be an operation or another right bracket
                if (index < formula.length-1 && formula[index+1] && formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.Operation &&
                formula[index+1].Descriptor !== AnalyticsFormulaTokenDescriptor.RightBracket) {
                    res = false
                    return
                }
                break;
        }

    })

    // left and right brackets counts should be equal
    if (lbr_count !== rbr_count) {
        res = false
    }

    return res
}

function validateDates(val: SelectionDateStampsFilterSend | SelectionProjectStageFilterSend) {
    if (val.EndDateIsToday) {
        if (val.StartDate && new Date(Date.parse(''+val.StartDate)) > new Date()) {
            return false
        } else if (val.EndDate) {
            return false
        }
    } else if (!val.EndDateIsToday && (!val.StartDate || !val.EndDate || 
        // (el.StartDate && new Date(Date.parse(''+el.StartDate)) > new Date()) || 
        // (el.EndDate && new Date(Date.parse(''+el.EndDate)) > new Date()) || 
        (val.StartDate && val.EndDate && new Date(Date.parse(''+val.StartDate)) > new Date(Date.parse(''+val.EndDate))) )) {
        return false
    }
    return true;
}

export function validateFilters(val: any): any[] {
    const res: any[] = []
    
    switch (val.Descriptor) {
        // Text filters
        case SelectionFilterGroupSendDescriptor.Text:
            val.Filters.forEach((el: any, index: number) => {
                if (el.Value === "") {
                    res.push({
                        filterDescriptor: val.Descriptor,
                        filterCategory: val.Category,
                        filterIndex: index,
                        errorText: i18n.global.t('filter_empty_error')
                    })
                }
            })
            break;
        // Date filters
        case SelectionFilterGroupSendDescriptor.Datestamps:
        case SelectionFilterGroupSendDescriptor.LastMovementOfStorageItem:
        case SelectionFilterGroupSendDescriptor.ProjectStages:
            // global date filter
            if (val.Descriptor === SelectionFilterGroupSendDescriptor.ProjectStages && 
                (val as SelectionProjectStageFilterGroupSend).CommonRange != null) {
                // @ts-ignore
                if (!validateDates((val as SelectionProjectStageFilterGroupSend).CommonRange)) {
                    res.push({
                        filterDescriptor: val.Descriptor,
                        filterCategory: val.Category,
                        filterIndex: 0,
                        errorText: i18n.global.t('dates_incorrect_error')
                    })
                }
            }
            val.Filters.forEach((el: SelectionDateStampsFilterSend, index: number) => {
                if (val.Descriptor !== SelectionFilterGroupSendDescriptor.ProjectStages || (el as SelectionProjectStageFilterSend).FilterType) {
                    if (!validateDates(el)) {
                        res.push({
                            filterDescriptor: val.Descriptor,
                            filterCategory: val.Category,
                            filterIndex: index,
                            errorText: i18n.global.t('dates_incorrect_error')
                        })
                    }
                }
            })
            break;
        // Entity with roles filters
        case SelectionFilterGroupSendDescriptor.EntityWithRoles:
            val.Filters.forEach((el: SelectionEntityWithRolesFilterSend, index: number) => {
                if (!el.Entity || !el.Entity.Id || !el.Entity.Name) {
                    res.push({
                        filterDescriptor: val.Descriptor,
                        filterCategory: val.Category,
                        filterIndex: index,
                        errorText: i18n.global.t('filter_empty_error')
                    })
                }
            })
            break;
        // Parameters filters
        case SelectionFilterGroupSendDescriptor.Parameters:
            // then recursive validation
            foreachRecursive(function a(element: any, el_to_find: any): any | undefined {
                if (!element) return
                const new_chain = [ ...el_to_find.chain, element.Parameter.Id ]
                // validation
                switch (element.Descriptor as SelectionParametersFilterSendDescriptor) {
                    // text parameter conditions
                    case SelectionParametersFilterSendDescriptor.String:
                        // empty parameter chains are not allowed - just parameters themselves
                        if (new_chain.length > 1 && (element as SelectionParameterFilterSend).Selected && !(element as SelectionParameterFilterStringSend).Conditions.length) {
                            res.push({
                                filterDescriptor: val.Descriptor,
                                filterCategory: val.Category,
                                conditionIndex: -1,
                                parameterLink: new_chain,
                                errorText: i18n.global.t('record_empty_error')
                            })
                        }
                        // each condition validation
                        (element as SelectionParameterFilterStringSend).Conditions.forEach((el, index: number) => {
                            /* if (el.Value === "") {
                                res.push({
                                    filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                    filterCategory: val.Category,
                                    conditionIndex: index,
                                    parameterLink: new_chain,
                                    errorText: i18n.global.t('filter_empty_error')
                                })
                            } */
                           // Allowed empty string filters
                        })
                        break;
                    // bool parameter conditions
                    case SelectionParametersFilterSendDescriptor.Bool:
                        // empty parameter chains are not allowed - just parameters themselves
                        if (new_chain.length > 1 && (element as SelectionParameterFilterBoolSend).Selected && (element as SelectionParameterFilterBoolSend).BoolFlags === 0) {
                            res.push({
                                filterDescriptor: val.Descriptor,
                                filterCategory: val.Category,
                                conditionIndex: -1,
                                parameterLink: new_chain,
                                errorText: i18n.global.t('record_empty_error')
                            })
                        }
                        break;
                    case SelectionParametersFilterSendDescriptor.DateStamps:
                        // empty parameter chains are not allowed - just parameters themselves
                        if (new_chain.length > 1 && (element as SelectionParameterFilterSend).Selected && !(element as SelectionParameterFilterDateStampsSend).Conditions.length) {
                            res.push({
                                filterDescriptor: val.Descriptor,
                                filterCategory: val.Category,
                                conditionIndex: -1,
                                parameterLink: new_chain,
                                errorText: i18n.global.t('record_empty_error')
                            })
                        }
                        // each condition validation
                        (element as SelectionParameterFilterDateStampsSend).Conditions.forEach((el: SelectionDateStampsFilterSend, index: number) => {
                            if (el.EndDateIsToday) {
                                if (!el.StartDate || el.StartDate > new Date()) {
                                    res.push({
                                        filterDescriptor: val.Descriptor,
                                        filterCategory: val.Category,
                                        filterIndex: index,
                                        errorText: i18n.global.t('start_date_error')
                                    })
                                } else if (el.EndDate) {
                                    res.push({
                                        filterDescriptor: val.Descriptor,
                                        filterCategory: val.Category,
                                        filterIndex: index,
                                        errorText: i18n.global.t('dates_incorrect_error')
                                    })
                                }
                            } else if (!el.EndDateIsToday && (!el.StartDate || !el.EndDate || 
                                el.StartDate > new Date() || // el.EndDate > new Date() || 
                                el.StartDate > el.EndDate)) {
                                res.push({
                                    filterDescriptor: val.Descriptor,
                                    filterCategory: val.Category,
                                    filterIndex: index,
                                    errorText: i18n.global.t('dates_incorrect_error')
                                })
                            }
                        })
                        break;
                    case SelectionParametersFilterSendDescriptor.StringSet:
                        // empty parameter chains are not allowed - just parameters themselves
                        if (new_chain.length > 1 && (element as SelectionParameterFilterSend).Selected && 
                            !(element as SelectionParameterFilterStringSetSend).TextConditions.length &&
                            !(element as SelectionParameterFilterStringSetSend).EntityConditions.length) {
                            res.push({
                                filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                filterCategory: val.Category,
                                conditionIndex: -1,
                                parameterLink: new_chain,
                                errorText: i18n.global.t('record_empty_error')
                            })
                        }
                        // each text condition validation
                        (element as SelectionParameterFilterStringSetSend).TextConditions.forEach((el, index: number) => {
                            /* if (el.Value === "") {
                                res.push({
                                    filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                    filterCategory: val.Category,
                                    conditionIndex: index,
                                    parameterLink: new_chain,
                                    errorText: i18n.global.t('filter_empty_error')
                                })
                            } */
                           // Allowed empty string parameter filters
                        });
                        // each entity condition validation
                        (element as SelectionParameterFilterStringSetSend).EntityConditions.forEach((el, index: number) => {
                            if (el.Entity && !el.Entity.Id) {
                                res.push({
                                    filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                    filterCategory: val.Category,
                                    conditionIndex: index,
                                    parameterLink: new_chain,
                                    errorText: i18n.global.t('filter_empty_error')
                                })
                            }
                        });
                        break;
                    // formula parameter conditions
                    case SelectionParametersFilterSendDescriptor.Number:
                        // empty parameter chains are not allowed - just parameters themselves
                        if (new_chain.length > 1 && (element as SelectionParameterFilterSend).Selected && !(element as SelectionParameterFilterNumberSend).Formulas.length) {
                            res.push({
                                filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                filterCategory: val.Category,
                                conditionIndex: -1,
                                parameterLink: new_chain,
                                errorText: i18n.global.t('record_empty_error')
                            })
                        }
                        // each condition validation
                        (element as SelectionParameterFilterNumberSend).Formulas.forEach((el, index: number) => {
                            /* if (!el.FormulaTokens || !el.FormulaTokens.length) {
                                res.push({
                                    filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                    filterCategory: val.Category,
                                    conditionIndex: index,
                                    parameterLink: new_chain,
                                    errorText: i18n.global.t('formula_empty_error')
                                })
                            } */
                           // Allowed empty formula parameter filters

                            if (!validateFormula(el.FormulaTokens)) {
                                console.log('push')
                                res.push({
                                    filterDescriptor: SelectionFilterGroupSendDescriptor.Parameters,
                                    filterCategory: val.Category,
                                    conditionIndex: index,
                                    parameterLink: new_chain,
                                    errorText: i18n.global.t('formula_incorrect_error')
                                })
                            }
                        })
                        break;
                    default:
                        break;
                }
                
                // child fields validation
                if (element.FieldFilters) {
                    foreachRecursive(a, {
                        el_to_find: el_to_find.el_to_find ? el_to_find.el_to_find : element, 
                        chain: new_chain
                    }, element.FieldFilters)
                }
                return
            }, { el_to_find: undefined, chain: [] }, val.Parameters)
            break;

            case SelectionFilterGroupSendDescriptor.StorageItemNames:
                val.Filters.forEach((el: SelectionStorageItemNameFilterDto, index: number) => {
                    // then recursive validation
                    if (el.Container && isInstanceContainerEmpty(el.Container, true) && !el.VendorCode) {

                    // if (allEmpty) {
                        console.log('push')
                        console.log(val)
                        res.push({
                            filterDescriptor: val.Descriptor,
                            filterCategory: val.Category,
                            conditionIndex: index,
                            errorText: i18n.global.t('filter_empty_error')
                        })
                    }
                });
            break;
        default:
            break;
    }

    return res
}

export function validateCharts(val: any, type: number, sector: string): any[] {
    const res: any[] = []
    if (!val || !val[sector]) {
        return res
    }

    switch (type) {
        case BaseItemTypes.BAR_CHART:
        case BaseItemTypes.PIE_CHART:
            console.log('validate charts')
            console.log(val)
            console.log(val[sector])

            val[sector] && val[sector].forEach((el: any, index: number) => {
                if (el) {
                    if (el.Descriptor === ChartSettingsBaseDescriptor.StorageItemBarChart ||
                    el.Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementBarChart || 
                    el.Descriptor === ChartSettingsBaseDescriptor.StorageItemPieChart ||
                    el.Descriptor === ChartSettingsBaseDescriptor.StorageItemMovementPieChart) {
                        if (el.SplitType === StorageItemChartSplitType.FinancialBurden ||
                        el.SplitType === StorageItemChartSplitType.ItemsQuantity ||
                        el.SplitType === StorageItemChartSplitType.SingleItemPrice ||
                        el.SplitType === StorageItemChartSplitType.TotalAmount) {
                            if (!el.NumberParameterBorders || !el.NumberParameterBorders.length) {
                                res.push({
                                    chartDescriptor: val.Descriptor,
                                    errorType: ChartValidationErrors.NUMBER_BORDERS,
                                    errorText: i18n.global.t('filter_empty_error')
                                })
                            }
                        } else if (el.SplitType == StorageItemChartSplitType.ItemNameField) {
                            if (!el.ParameterLinkForSplit || !el.ParameterLinkForSplit.IdChain || !el.ParameterLinkForSplit.IdChain.length) {
                                res.push({
                                    chartDescriptor: val.Descriptor,
                                    errorType: ChartValidationErrors.NUMBER_BORDERS,
                                    errorText: i18n.global.t('filter_empty_error')
                                })
                            }
                        }
                    } else if (el.Descriptor === ChartSettingsBaseDescriptor.ClientBarChart && el.SplitType === ClientBarChartSplitType.Parameter || 
                    el.Descriptor === ChartSettingsBaseDescriptor.ProjectBarChart && el.SplitType === ProjectBarChartSplitType.Parameter || 
                    el.Descriptor === ChartSettingsBaseDescriptor.ClientPieChart && el.SplitType === ClientPieChartSplitType.Parameter || 
                    el.Descriptor === ChartSettingsBaseDescriptor.ProjectPieChart && el.SplitType === ProjectPieChartSplitType.Parameter) { 
                        if (!el.ParameterLink || !el.ParameterLink.IdChain || !el.ParameterLink.IdChain.length) {
                            res.push({
                                chartDescriptor: val.Descriptor,
                                errorType: ChartValidationErrors.NUMBER_BORDERS,
                                errorText: i18n.global.t('filter_empty_error')
                            })
                        } else {
                            if (el.Descriptor === ChartSettingsBaseDescriptor.ClientBarChart && el.SplitType === ClientBarChartSplitType.Parameter || 
                            el.Descriptor === ChartSettingsBaseDescriptor.ProjectBarChart && el.SplitType === ProjectBarChartSplitType.Parameter || 
                            el.Descriptor === ChartSettingsBaseDescriptor.ClientPieChart && el.SplitType === ClientPieChartSplitType.Parameter || 
                            el.Descriptor === ChartSettingsBaseDescriptor.ProjectPieChart && el.SplitType === ProjectPieChartSplitType.Parameter) {

                                const param = findRecursive(function a(element: any, el_to_find: any): any | undefined {
                                if (!element) return undefined

                                if (arrayEquals([ ...el_to_find.chain, element.Id ], el_to_find.el_to_find)) {
                                    return element;
                                }
                                if (element['RecordFields']) {
                                    return findRecursive(a, {
                                    chain: [ ...el_to_find.chain, element.Id ], 
                                    el_to_find: el_to_find.el_to_find
                                    }, element['RecordFields'])
                                }
                                return undefined
                                }, { chain: [], el_to_find: el.ParameterLink.IdChain }, 
                                (val as ClientProjectBarTypes).Parameters)

                                if ([0,1,2,4,5].includes(param ? param.Type : 0) && (!el.NumberParameterBorders || !el.NumberParameterBorders.length)) {
                                    res.push({
                                        chartDescriptor: val.Descriptor,
                                        errorType: ChartValidationErrors.NUMBER_BORDERS,
                                        errorText: i18n.global.t('filter_empty_error')
                                    })
                                }
                            }
                        }
                    } else if (el.Descriptor === ChartSettingsBaseDescriptor.ClientBarChart && el.SplitType === ClientBarChartSplitType.Address ||
                    el.Descriptor === ChartSettingsBaseDescriptor.ClientPieChart && el.SplitType === ClientPieChartSplitType.Address ||
                    el.Descriptor === ChartSettingsBaseDescriptor.PersonBarChart && el.SplitType === PersonBarChartSplitType.Address ||
                    el.Descriptor === ChartSettingsBaseDescriptor.PersonPieChart && el.SplitType === PersonPieChartSplitType.Address) {
                        if (!el.AddressLevelId) {
                            res.push({
                                chartDescriptor: val.Descriptor,
                                errorType: ChartValidationErrors.NUMBER_BORDERS,
                                errorText: i18n.global.t('filter_empty_error')
                            })
                        }
                    } else if (el.Descriptor === ChartSettingsBaseDescriptor.ProjectBarChart && el.SplitType === ProjectBarChartSplitType.AddressInRole || 
                        el.Descriptor === ChartSettingsBaseDescriptor.JobBarChart && el.SplitType === JobBarChartSplitType.AddressInRole ||
                        el.Descriptor === ChartSettingsBaseDescriptor.FileBarChart && el.SplitType === FileBarChartSplitType.AddressInRole ||
                        el.Descriptor === ChartSettingsBaseDescriptor.ProjectPieChart && el.SplitType === ProjectPieChartSplitType.AddressInRole || 
                        el.Descriptor === ChartSettingsBaseDescriptor.JobPieChart && el.SplitType === JobPieChartSplitType.AddressInRole ||
                        el.Descriptor === ChartSettingsBaseDescriptor.FilePieChart && el.SplitType === FilePieChartSplitType.AddressInRole) {
                        if (!el.AddressLevelId) {
                            res.push({
                                chartDescriptor: val.Descriptor,
                                errorType: ChartValidationErrors.NUMBER_BORDERS,
                                errorText: i18n.global.t('filter_empty_error')
                            })
                        }
                    }
                }
            });

            break;
        case BaseItemTypes.GRAPH:
            // console.log('validate charts')
            // console.log(val)
            // console.log(val[sector])

            // Шаг по оси X: если пользователь стёр значение (пусто/не число/меньше 1),
            // настройки не сохраняем — иначе на бэк ушёл бы некорректный шаг.
            if (val.StepLength === null || val.StepLength === undefined || (val.StepLength as any) === "" ||
                !isFinite(Number(val.StepLength)) || Number(val.StepLength) < 1) {
                res.push({
                    chartDescriptor: val.Descriptor,
                    errorType: ChartValidationErrors.STEP_LENGTH,
                    errorText: i18n.global.t('x_step_error')
                })
            }

            if (val.DateRangeEndIsToday) {
                if (!val.DateRangeStart || val.DateRangeStart > new Date()) {
                    res.push({
                        chartDescriptor: val.Descriptor,
                        errorType: ChartValidationErrors.DATE_RANGE,
                        errorText: i18n.global.t('start_date_error')
                    })
                } else if (val.DateRangeEnd) {
                    res.push({
                        chartDescriptor: val.Descriptor,
                        errorType: ChartValidationErrors.DATE_RANGE,
                        errorText: i18n.global.t('dates_incorrect_error')
                    })
                }
            } else if (!val.DateRangeEndIsToday && (!val.DateRangeStart || !val.DateRangeEnd || 
                val.DateRangeStart > new Date() || // val.DateRangeEnd > new Date() || 
                val.DateRangeStart > val.DateRangeEnd)) {
                res.push({
                    chartDescriptor: val.Descriptor,
                    errorType: ChartValidationErrors.DATE_RANGE,
                    errorText: i18n.global.t('dates_incorrect_error')
                })
            }
            break;
        default:
            break;
    }

    return res
}

export function validateFormulas(val: any, allowEmpty: boolean = false): any[] {
    const res: any[] = []
    val && val.forEach((el: AnalyticsTableFormula, index: number) => {
        if (el) {
            if (!allowEmpty && (!el.FormulaTokens || !el.FormulaTokens.length)) {
                res.push({
                    filterDescriptor: 'table_formulas',
                    conditionIndex: index,
                    formulaId: el.Id,
                    errorText: i18n.global.t('formula_empty_error')
                })
            }
            
            if (el && !validateFormula(el.FormulaTokens)) {
                console.log('push')
                res.push({
                    filterDescriptor: 'table_formulas',
                    conditionIndex: index,
                    formulaId: el.Id,
                    errorText: i18n.global.t('formula_incorrect_error')
                })
            }
        }
    })
    return res
}

